import type { ColDef } from "ag-grid-community";
import { type CustomFilterProps, useGridFilter } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import type { MultiSelectFilterModel } from "src/pages/Merchflows/store/agGridModelMapper";
import { Checkbox, Color, Flex, Text } from "src/elements";

type Props = CustomFilterProps<unknown, unknown, MultiSelectFilterModel>;

type MultiSelectOption = {
  value: string;
  label: string;
};

type MultiSelectFilterParams = {
  options?: Array<string | MultiSelectOption>;
  selectAllLabel?: string;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 220px;
  padding: 8px;
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 240px;
  overflow-y: auto;
  padding-right: 4px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: end;
  gap: 8px;
`;

const ActionButton = styled.button`
  border: 1px solid ${Color.lightGray};
  border-radius: 4px;
  background: ${Color.white};
  padding: 4px 10px;
  cursor: pointer;
`;

const getFilterParams = (colDef: ColDef): MultiSelectFilterParams => {
  return (colDef.filterParams as MultiSelectFilterParams | undefined) || {};
};

const normalizeValues = (values: readonly string[] | undefined) => {
  if (!values?.length) {
    return [];
  }

  return values
    .filter((value): value is string => typeof value === "string" && value !== "")
    .filter((value, index, array) => array.indexOf(value) === index);
};

const modelToDraftValues = (model: MultiSelectFilterModel | null | undefined) => {
  return normalizeValues(model?.values);
};

const toCheckboxTestId = (value: string) => {
  return `multi-select-filter-option-${value.replace(/[^a-zA-Z0-9_-]+/g, "-")}`;
};

export const MultiSelectFilter = ({ model, onModelChange, colDef }: Props) => {
  const filterParams = useMemo(() => getFilterParams(colDef), [colDef]);
  const options = useMemo(
    () =>
      (filterParams.options || []).map((option) =>
        typeof option === "string" ? { value: option, label: option } : option,
      ),
    [filterParams.options],
  );
  const selectAllLabel = filterParams.selectAllLabel || "Select all";
  const [draftValues, setDraftValues] = useState(modelToDraftValues(model));

  const optionValueSet = useMemo(() => new Set(options.map((option) => option.value)), [options]);

  useEffect(() => {
    setDraftValues(modelToDraftValues(model));
  }, [model]);

  useEffect(() => {
    setDraftValues((currentValues) =>
      options
        .map((option) => option.value)
        .filter((optionValue) => currentValues.includes(optionValue)),
    );
  }, [options]);

  const getModelLabels = useCallback(
    (values: readonly string[]) => {
      const labelsByValue = new Map(options.map((option) => [option.value, option.label]));
      return values.map((value) => labelsByValue.get(value) || value).join(", ");
    },
    [options],
  );

  useGridFilter({
    doesFilterPass: () => true,
    getModelAsString: (currentModel) => {
      const currentValues = normalizeValues(
        (currentModel as MultiSelectFilterModel | null)?.values,
      ).filter((value) => optionValueSet.has(value));

      return getModelLabels(currentValues);
    },
  });

  const selectAllState =
    draftValues.length === 0
      ? false
      : draftValues.length === options.length && options.length > 0
        ? true
        : "mixed";

  const toggleValue = (value: string, isChecked: boolean) => {
    setDraftValues((currentValues) => {
      const nextValuesSet = new Set(currentValues);

      if (isChecked) {
        nextValuesSet.add(value);
      } else {
        nextValuesSet.delete(value);
      }

      return options
        .map((option) => option.value)
        .filter((optionValue) => nextValuesSet.has(optionValue));
    });
  };

  const apply = () => {
    if (draftValues.length === 0) {
      onModelChange(null);
      return;
    }

    onModelChange({
      filterType: "multiSelect",
      values: draftValues,
    });
  };

  const clear = () => {
    setDraftValues([]);
    onModelChange(null);
  };

  return (
    <Container>
      <Options>
        <Checkbox
          name="multi-select-filter-select-all"
          label={selectAllLabel}
          isCheckboxLeft
          isChecked={selectAllState}
          setIsChecked={(isChecked) =>
            setDraftValues(isChecked ? options.map((option) => option.value) : [])
          }
        />

        {options.length === 0 ? (
          <Flex padding="4px 0">
            <Text variant="small2" color={Color.textSecondary}>
              No options available
            </Text>
          </Flex>
        ) : (
          options.map((option) => (
            <Checkbox
              key={option.value}
              name={toCheckboxTestId(option.value)}
              label={option.label}
              isCheckboxLeft
              isChecked={draftValues.includes(option.value)}
              setIsChecked={(isChecked) => toggleValue(option.value, isChecked)}
            />
          ))
        )}
      </Options>

      <Actions>
        <ActionButton type="button" onClick={clear}>
          Clear
        </ActionButton>

        <ActionButton type="button" onClick={apply}>
          Apply
        </ActionButton>
      </Actions>
    </Container>
  );
};
